<template>
  <div class="h-100" style="min-height: 1px">
    <b-overlay :blur="null" :show="loading" class="h-100" opacity="0">
      <transition mode="out-in" name="fadeDownShort">
        <div v-if="!loading" ref="scroll" v-bar>
          <div>
            <b-row>
              <b-col md="4">
                <b-card class="card-shadow mb-4" no-body>
                  <b-card-body>
                    <div class="d-flex justify-content-between">
                      <b-form-group label-for="year">
                        <b-form inline>
                          <label class="font-weight-bold mr-2">Rok:</label>
                          <b-form-select
                            v-model="activeDate.year"
                            :select-size="1"
                            @change="changeYear"
                          >
                            <option
                              v-for="year in reportYears"
                              :key="year"
                              :value="year"
                            >
                              {{ year }}
                            </option>
                          </b-form-select>
                        </b-form>
                      </b-form-group>
                    </div>
                    <div class="calendar">
                      <div class="cal-body month-view">
                        <div
                          v-for="(month, i) in $helpers.months()"
                          :key="i"
                          :class="{ active: activeDate.month == i + 1 }"
                          class="day-cell is-month p-2"
                          @click="changeMonth(i + 1)"
                        >
                          <span class="day">{{ $helpers.monthNames(i) }}</span>
                          <div
                            v-if="monthConsumption[i + 1] >= 0"
                            :style="
                              'background-color:' +
                              interpolateColors[i + 1].color
                            "
                            class="fs-sm my-1 p-1"
                            style="line-height: 1"
                          >
                            {{ monthConsumption[i + 1] | formatNumber(2) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </b-card-body>
                </b-card>
              </b-col>
              <b-col md="8">
                <transition mode="out-in" name="fadeDownShort">
                  <b-card
                    v-if="monthData.length"
                    key="1"
                    class="card-shadow mb-4"
                    header-class="bg-white"
                    no-body
                  >
                    <b-card-body>
                      <b-tabs
                        nav-class="nav-sub nav-sm mt-n1 mb-3"
                        no-nav-style
                        @input="$vuebar.refreshScrollbar($refs.scroll, {})"
                      >
                        <template slot="tabs-end">
                          <b-btn
                            class="ml-3 mb-2"
                            size="sm"
                            variant="secondary"
                            @click="exportValues"
                          >
                            <b-icon
                              class="mr-1"
                              icon="file-earmark-spreadsheet"
                            />
                            Exportovať
                          </b-btn>
                        </template>
                        <b-tab active title="Graf">
                          <month-chart
                            v-if="monthData.length"
                            :month-chart="chartData(monthData)"
                          />
                        </b-tab>
                        <b-tab title="Intervalové data">
                          <client-table
                            table-class="table-sm"
                            :columns="table.columns"
                            :data="monthData"
                            :searchable="false"
                            :sort-by.sync="table.sortBy"
                            :tbody-tr-class="rowWeekend"
                            bordered
                            striped
                          >
                            <template #custom-foot>
                              <b-tr
                                variant="primary"
                                style="border-top: 5px solid #b5cf9c"
                              >
                                <template v-for="(col, i) in table.columns">
                                  <b-th v-if="col.key == 'date'" :key="i">
                                    Celkom
                                  </b-th>
                                  <template v-else>
                                    <b-td
                                      v-if="col.key == 'combustionHeat'"
                                      :key="i"
                                    />
                                    <b-td
                                      v-else
                                      :key="i"
                                      class="text-right text-nowrap"
                                    >
                                      {{
                                        monthSummary.total[col.key]
                                          | formatNumber(2)
                                      }}
                                    </b-td>
                                  </template>
                                </template>
                              </b-tr>
                              <b-tr variant="primary">
                                <template v-for="(col, i) in table.columns">
                                  <b-th v-if="col.key == 'date'" :key="i">
                                    Minimum
                                  </b-th>
                                  <template v-else>
                                    <b-td
                                      v-if="col.key == 'combustionHeat'"
                                      :key="i"
                                    />
                                    <b-td
                                      v-else
                                      :key="i"
                                      class="text-right text-nowrap"
                                    >
                                      {{
                                        monthSummary.min[col.key]
                                          | formatNumber(2)
                                      }}
                                    </b-td>
                                  </template>
                                </template>
                              </b-tr>
                              <b-tr variant="primary">
                                <template v-for="(col, i) in table.columns">
                                  <b-th v-if="col.key == 'date'" :key="i">
                                    Maximum
                                  </b-th>
                                  <template v-else>
                                    <b-td
                                      v-if="col.key == 'combustionHeat'"
                                      :key="i"
                                    />
                                    <b-td
                                      v-else
                                      :key="i"
                                      class="text-right text-nowrap"
                                    >
                                      {{
                                        monthSummary.max[col.key]
                                          | formatNumber(2)
                                      }}
                                    </b-td>
                                  </template>
                                </template>
                              </b-tr>
                            </template>
                          </client-table>
                        </b-tab>
                      </b-tabs>
                    </b-card-body>
                  </b-card>
                  <div v-else key="2" class="text-center my-3">
                    <div class="mb-3">
                      <b-icon font-scale="5" icon="question-diamond" />
                    </div>
                    <h2 class="mb-3">
                      V danom období neboli nájdené žiadne dáta
                    </h2>
                  </div>
                </transition>
              </b-col>
            </b-row>
            <transition name="fadeUpShort">
              <year-chart
                v-if="yearData.length"
                v-model="yearChartType"
                :date="activeDate"
                :loading="yearLoading"
                :year-chart="chartData(yearData)"
              />
            </transition>
          </div>
        </div>
      </transition>
    </b-overlay>
  </div>
</template>
<script>
import ClientTable from '@/components/ClientTable'
import MonthChart from './MonthChart'
import YearChart from './YearChart'
import collect from 'collect.js'

export default {
  components: {
    ClientTable,
    MonthChart,
    YearChart,
  },
  data() {
    return {
      data: [],
      yearData: [],
      yearChartType: 'year',
      loading: false,
      yearLoading: false,
      reportYears: [],
      activeDate: {
        year: this.$moment().year(),
        month: this.$moment().month() + 1,
      },
      table: {
        columns: [
          {
            key: 'date',
            label: 'Dátum',
            formatter: (value) =>
              value ? this.$options.filters.formatDate(value) : '-',
            sortable: true,
          },
          {
            key: 'consumption',
            label: 'Odber',
            class: 'text-right',
            tdClass: 'text-nowrap',
            formatter: (value) =>
              value !== null
                ? this.$options.filters.formatNumber(value, 2)
                : '-',
            sortable: true,
          },
          {
            key: 'combustionHeat',
            label: 'Spaľovacie teplo',
            class: 'text-right',
            tdClass: 'text-nowrap',
            formatter: (value) =>
              value !== null
                ? this.$options.filters.formatNumber(value, 3)
                : '-',
            sortable: true,
          },
          {
            key: 'border100percent',
            label: 'Hranica 100%',
            class: 'text-right',
            tdClass: 'text-nowrap',
            formatter: (value) =>
              value !== null
                ? this.$options.filters.formatNumber(value, 2)
                : '-',
            sortable: true,
          },
          {
            key: 'tolerance100to105percent',
            label: 'Tolerancia 100% - 105%',
            class: 'text-right',
            tdClass: 'text-nowrap',
            formatter: (value) =>
              value !== null
                ? this.$options.filters.formatNumber(value, 2)
                : '-',
            sortable: true,
          },
          {
            key: 'overLimit105to110percent',
            label: 'Nadlimit 105% - 110%',
            class: 'text-right',
            tdClass: 'text-nowrap',
            formatter: (value) =>
              value !== null
                ? this.$options.filters.formatNumber(value, 2)
                : '-',
            sortable: true,
          },
          {
            key: 'overLimitOver110percent',
            label: 'Nadlimit nad 110%',
            class: 'text-right',
            tdClass: 'text-nowrap',
            formatter: (value) =>
              value !== null
                ? this.$options.filters.formatNumber(value, 2)
                : '-',
            sortable: true,
          },
          {
            key: 'sumOverLimit105percent',
            label: 'Suma nad limit 105%',
            class: 'text-right',
            tdClass: 'text-nowrap',
            formatter: (value) =>
              value !== null
                ? this.$options.filters.formatNumber(value, 2)
                : '-',
            sortable: true,
          },
        ],
        sortBy: 'date',
      },
    }
  },
  computed: {
    interpolateColors() {
      let gradient = [
        '#BCDDF5',
        '#AFD6F3',
        '#A2D0F1',
        '#95C9EF',
        '#89C3ED',
        '#7CBCEB',
        '#6FB6EA',
        '#62AFE8',
        '#56A9E6',
        '#49A2E4',
        '#3C9CE2',
        '#3096E1',
      ]
      let months = []
      let i = 0
      for (let month in this.monthConsumption) {
        months[i++] = {
          month: month,
          value: this.monthConsumption[month],
        }
      }
      return collect(months)
        .sortBy('value')
        .map((item, i) => {
          return {
            month: item.month,
            value: item.value,
            color: gradient[i],
          }
        })
        .keyBy('month')
        .all()
    },
    monthData() {
      let data = []
      data = this.data.filter((month) => {
        return (
          parseInt(this.$moment(month.date).format('M')) ===
          this.activeDate.month
        )
      })
      return data
    },
    monthConsumption() {
      let data = {}
      data = collect(this.data)
        .groupBy((item) => {
          return parseInt(this.$moment(item.date).format('M'))
        })
        .map((val) => {
          return val.reduce((sum, item) => item.consumption + sum)
        })
        .all()

      return data
    },
    monthSummary() {
      let data = {
        total: {},
        min: {},
        max: {},
      }
      let md = collect(this.monthData)
      collect(this.table.columns.map((i) => i.key))
        .except(['date', 'combustionHeat'])
        .each((item) => {
          data.total[item] = md.whereNotNull(item).sum(item)
          data.min[item] = md.whereNotNull(item).min(item)
          data.max[item] = md.whereNotNull(item).max(item)
        })
        .all()

      return data
    },
  },
  watch: {
    yearChartType() {
      this.fetchData(true, true)
    },
  },
  created() {
    this.loading = true
    this.$api
      .get(
        `reports/${this.$route.params.energetics_id}/interval/get_gas_available_date`,
      )
      .then((res) => {
        this.reportYears = res.data.data.years
        let date = this.$moment(res.data.data.latest)
        this.activeDate = {
          year: parseInt(date.format('YYYY')),
          month: parseInt(date.format('MM')),
        }
        this.fetchData()
      })
  },
  methods: {
    fetchData(silent = false, year = false) {
      this.loading = !silent
      this.$api
        .get(
          `reports/${this.$route.params.energetics_id}/interval/get_gas_data`,
          {
            params: {
              year:
                this.yearChartType === 'prevyear'
                  ? this.$moment().subtract(1, 'year').year()
                  : this.activeDate.year,
              //month: this.activeDate.month
            },
          },
        )
        .then((res) => {
          if (!year) {
            this.data = res.data
          }
          this.yearData = res.data
          if (!silent) {
            this.loading = false
          }
        })
    },
    changeYear() {
      this.fetchData()
    },
    changeMonth(month) {
      this.activeDate.month = month
      this.fetchData(true)
    },
    chartData(data = []) {
      if (data.length) {
        data = collect(data).sortBy('date').all()
        let output = {
          border100: [],
          values: [],
          tolerance100to105: [],
          overLimit105to110: [],
          overLimitOver110: [],
        }
        data.forEach((day, i) => {
          let date = this.$moment(day.date).valueOf()
          output.border100[i] = {
            x: date,
            y: day.border100percent,
          }
          output.tolerance100to105[i] = {
            x: date,
            y: day.tolerance100to105percent,
          }
          output.overLimit105to110[i] = {
            x: date,
            y: day.overLimit105to110percent,
          }
          output.overLimitOver110[i] = {
            x: date,
            y: day.overLimitOver110percent,
          }
          output.values[i] = {
            x: date,
            y: day.consumption,
          }
        })
        return output
      } else {
        return {}
      }
    },
    rowWeekend(item, type) {
      if (!item || type !== 'row') return
      if (this.$moment(item.date).isoWeekday() >= 6) {
        return 'table-secondary'
      }
    },
    exportValues() {
      this.$swal({
        toast: true,
        type: 'info',
        position: 'top-end',
        title: 'Pripravujem export...',
      })
      this.$api
        .get('energy_data/export_gas', {
          params: {
            id: this.$route.params.energetics_id,
            year: this.activeDate.year,
            month: this.activeDate.month,
          },
          responseType: 'blob',
        })
        .then((res) => {
          saveAs(
            new Blob([res.data]),
            'intervalove-data_plyn-' +
              this.activeDate.month +
              '/' +
              this.activeDate.year +
              '.xlsx',
          )
          this.$swal({
            toast: true,
            type: 'success',
            position: 'top-end',
            title: 'Export pripravený',
            timer: 5000,
          })
        })
        .catch(() => {
          this.$swal({
            toast: true,
            type: 'error',
            position: 'top-end',
            title: 'Chyba pri exportovaní',
            timer: 5000,
          })
        })
    },
  },
}
</script>
